import { createContext, FunctionComponent, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { ADAPTER_EVENTS, SafeEventEmitterProvider, WALLET_ADAPTERS, WALLET_ADAPTER_TYPE } from "@web3auth/base";
import type { LOGIN_PROVIDER_TYPE } from "@toruslabs/openlogin";

import { Web3AuthCore } from "@web3auth/core";
import { OpenloginAdapter } from "@web3auth/openlogin-adapter";
import { WalletConnectV1Adapter } from "@web3auth/wallet-connect-v1-adapter";
import { MetamaskAdapter } from "@web3auth/metamask-adapter";
import { NetworkSwitch } from "@web3auth/ui"
import { CHAIN_CONFIG, CHAIN_CONFIG_TYPE } from "./chainConfig";
import { WEB3AUTH_NETWORK_TYPE } from "./web3AuthNetwork";
import { getWalletProvider, IWalletProvider } from "./walletProvider";
import QRCodeModal from "@walletconnect/qrcode-modal";

export type { LOGIN_PROVIDER_TYPE } from "@toruslabs/openlogin";

export interface IWeb3AuthContext {
  web3Auth: Web3AuthCore | null;
  provider: IWalletProvider | null;
  isLoading: boolean;
  user: unknown;
  autoConnect?: boolean;
  login: (adapter: WALLET_ADAPTER_TYPE, provider?: LOGIN_PROVIDER_TYPE, login_hint?: string) => Promise<any>;
  loginWithWalletConnect: () => Promise<void>;
  logout: () => Promise<void>;
  getUserInfo: () => Promise<any>;
  signMessage: (message: string) => Promise<any>;
  signV4Message: () => Promise<any>;
  getAccounts: () => Promise<any>;
  getBalance: () => Promise<any>;
}

export const Web3AuthContext = createContext<IWeb3AuthContext>({
  web3Auth: null,
  provider: null,
  isLoading: false,
  user: null,
  autoConnect: false,
  login: async (_adapter: WALLET_ADAPTER_TYPE, _provider?: LOGIN_PROVIDER_TYPE, _login_hint?: string): Promise<SafeEventEmitterProvider | null> => { return null },
  loginWithWalletConnect: async () => { },
  logout: async () => { },
  getUserInfo: async () => { },
  signMessage: async () => { },
  signV4Message: async () => { },
  getAccounts: async () => { },
  getBalance: async () => { },
});

export function useWeb3Auth() {
  return useContext(Web3AuthContext);
}

interface IWeb3AuthState {
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  chain: CHAIN_CONFIG_TYPE;
  children?: ReactNode;
}
export interface IWeb3AuthProps {
  children?: ReactNode;
  web3AuthNetwork: WEB3AUTH_NETWORK_TYPE;
  chain: CHAIN_CONFIG_TYPE;
  autoConnect?: boolean;
  jsObject?: boolean;
  clientId?: string;
}

export const ADAPTER_CACHE_KEY = "Web3Auth-cachedAdapter";

export const Web3AuthProvider: FunctionComponent<IWeb3AuthState> = ({ children, web3AuthNetwork, chain, autoConnect, jsObject }: IWeb3AuthProps) => {
  const [web3Auth, setWeb3Auth] = useState<Web3AuthCore | null>(null);
  const [provider, setProvider] = useState<IWalletProvider | null>(null);
  const [user, setUser] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const setWalletProvider = useCallback(
    (web3authProvider: SafeEventEmitterProvider) => {
      const walletProvider = getWalletProvider(chain, web3authProvider, uiConsole);
      setProvider(walletProvider);
    },
    [chain]
  );

  useEffect(() => {
    const subscribeAuthEvents = (web3auth: Web3AuthCore) => {
      // Can subscribe to all ADAPTER_EVENTS and LOGIN_MODAL_EVENTS
      web3auth.on(ADAPTER_EVENTS.CONNECTED, (data: unknown) => {
        console.log("Yeah!, you are successfully logged in", data);
        setUser(data);
        setWalletProvider(web3auth.provider!);
      });

      web3auth.on(ADAPTER_EVENTS.CONNECTING, () => {
        console.log("connecting");
      });

      web3auth.on(ADAPTER_EVENTS.DISCONNECTED, () => {
        console.log("disconnected");
        setProvider(null);
        setUser(null);
      });

      web3auth.on(ADAPTER_EVENTS.ADAPTER_DATA_UPDATED, (data: unknown) => {
        console.log("ADAPTER_DATA_UPDATED", data);
      });

      web3auth.on(ADAPTER_EVENTS.ERRORED, (error: unknown) => {
        console.error("some error or user has cancelled login request", error);
      });
    };

    const currentChainConfig = CHAIN_CONFIG[chain];

    async function init() {
      try {
        setIsLoading(true);
        const clientId = "BMOX818DnItYrf06YfCdWRfKHgGOqh4D3vy0zcmJxKFtklrFKp5z3KbUMDQ4zg0sDNR8HyAfe3_jRX8TNEkY5N4";
        if(!autoConnect){
          localStorage.removeItem(ADAPTER_CACHE_KEY)
        }
        const web3AuthInstance = new Web3AuthCore({
          chainConfig: currentChainConfig,
          enableLogging: true,
          clientId
        });
        subscribeAuthEvents(web3AuthInstance);
        const networkUi = new NetworkSwitch()
        const metamaskAdapter = new MetamaskAdapter({ chainConfig: currentChainConfig, clientId });
        const adapter = new OpenloginAdapter({ adapterSettings: { network: web3AuthNetwork, clientId } });
        const wcAdapter = new WalletConnectV1Adapter({ adapterSettings: { qrcodeModal: QRCodeModal, networkSwitchModal: networkUi }, chainConfig: currentChainConfig })

        web3AuthInstance.configureAdapter(metamaskAdapter);
        web3AuthInstance.configureAdapter(adapter);
        web3AuthInstance.configureAdapter(wcAdapter);

        await web3AuthInstance.init();
        setWeb3Auth(web3AuthInstance);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, [chain, web3AuthNetwork, setWalletProvider]);

  const login = async (adapter: WALLET_ADAPTER_TYPE, loginProvider?: LOGIN_PROVIDER_TYPE, login_hint?: string) => {
    try {
      setIsLoading(true);
      if (!web3Auth) {
        console.log("web3auth not initialized yet");
        uiConsole("web3auth not initialized yet");
        return;
      }
      let localProvider;
      if(adapter === WALLET_ADAPTERS.METAMASK){
        localProvider = await web3Auth.connectTo(adapter);
      } else {
        localProvider = await web3Auth.connectTo(adapter, { loginProvider, login_hint });
      }
      setWalletProvider(localProvider!);
    } catch (error) {
      console.log("error", error);
      throw error;
    } finally {
      setIsLoading(false)
    }
  };
  const loginWithWalletConnect = async () => {
    try {
      setIsLoading(true);
      if (!web3Auth) {
        console.log("web3auth not initialized yet");
        uiConsole("web3auth not initialized yet");
        return;
      }
      const localProvider = await web3Auth.connectTo(WALLET_ADAPTERS.WALLET_CONNECT_V1, {});
      setWalletProvider(localProvider!);
    } catch (error) {
      console.log("error", error);
      throw error;
    } finally {
      setIsLoading(false)
    }
  };

  const logout = async () => {
    if (!web3Auth) {
      console.log("web3auth not initialized yet");
      uiConsole("web3auth not initialized yet");
      return;
    }
    await web3Auth.logout();
    setProvider(null);
  };

  const getUserInfo = async () => {
    if (!web3Auth) {
      console.log("web3auth not initialized yet");
      uiConsole("web3auth not initialized yet");
      return;
    }
    const user = await web3Auth.getUserInfo();
    uiConsole(user);
  };

  const getAccounts = async () => {
    if (!provider) {
      console.log("provider not initialized yet");
      uiConsole("provider not initialized yet");
      return;
    }
    return provider.getAccounts();
  };

  const getBalance = async () => {
    if (!provider) {
      console.log("provider not initialized yet");
      uiConsole("provider not initialized yet");
      return;
    }
    return provider.getBalance();
  };

  const signMessage = async (message: string) => {
    if (!provider) {
      console.log("provider not initialized yet");
      uiConsole("provider not initialized yet");
      return;
    }
    provider.signMessage(message);
  };
  const signV4Message = async () => {
    if (!provider) {
      console.log("provider not initialized yet");
      uiConsole("provider not initialized yet");
      return;
    }
    provider.signV4Message();
  };

  const uiConsole = (...args: unknown[]): void => {
    const el = document.querySelector("#console>p");
    if (el) {
      el.innerHTML = JSON.stringify(args || {}, null, 2);
    }
  };

  const contextProvider = {
    web3Auth,
    provider,
    user,
    isLoading,
    autoConnect,
    login,
    loginWithWalletConnect,
    logout,
    getUserInfo,
    getAccounts,
    getBalance,
    signMessage,
    signV4Message,
  };
  return contextProvider;

};

Web3AuthProvider.defaultProps = {
  chain: 'mainnet',
}

